import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectItemProps, Response, Pagination } from '@ui/src';
import { Device, DeviceSession, EmployeeProfile, Role } from '../types';
import { profileApi } from './services';

export interface EmployeeProfileState {
  employeeProfile?: EmployeeProfile;
  roles?: SelectItemProps[];
  devices?: Device[];
  devicesPagination?: Pagination;
  browserNotificationEnabled?: boolean;
  pspPartnerAccounts?: SelectItemProps[];
  deviceSessions?: DeviceSession[];
  deviceSessionsPagination?: Pagination;
}

const initialState: EmployeeProfileState = {};

export const employeeProfileSlice = createSlice({
  name: 'employeeProfile',
  initialState,
  reducers: {
    setBrowserNotificationEnabled: (state, action: PayloadAction<boolean>) => {
      state.browserNotificationEnabled = action.payload;
    },
    clearDevices: (state) => {
      state.devices = undefined;
      state.devicesPagination = undefined;
    },
    clearDeviceSession: (state) => {
      state.deviceSessions = undefined;
      state.deviceSessionsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      profileApi.endpoints.getEmployeeProfile.matchFulfilled,
      (state, action: PayloadAction<{ data: EmployeeProfile }>) => {
        state.employeeProfile = action.payload.data;
      },
    );
    builder.addMatcher(
      profileApi.endpoints.getEmpoyeeRoles.matchFulfilled,
      (state, action: PayloadAction<Response<Role>>) => {
        state.roles = action.payload.data.edges.map((item) => ({
          name: item.node.name,
          value: item.node.id,
        }));
      },
    );
    builder.addMatcher(
      profileApi.endpoints.endSession.matchFulfilled,
      (state, action: PayloadAction<{ data: Device }>) => {
        state.devices = state.devices?.filter(
          (item) => item.id !== action.payload.data.id,
        );
      },
    );
    builder.addMatcher(profileApi.endpoints.logout.matchFulfilled, () => {
      localStorage.removeItem('activePermissions');
      localStorage.removeItem('userId');
    });
    builder.addMatcher(
      profileApi.endpoints.getPspPartnerAccounts.matchFulfilled,
      (state, action: PayloadAction<Response<any>>) => {
        state.pspPartnerAccounts = action.payload?.data?.edges
          ?.filter(
            (item) => !item?.node?.account?.description.includes('funds'),
          )
          .map((item) => ({
            value: item.node.account?.fullNumber || '',
            name: item.node.description || '',
            description: '',
            suffix: item.node.account?.fullNumber,
          }))
          .sort(
            (a, b) =>
              Number(a.value.replaceAll('.', '')) -
              Number(b.value.replaceAll('.', '')),
          );
      },
    );
    builder.addMatcher(
      profileApi.endpoints.getEmployeeDevices.matchFulfilled,
      (state, action: PayloadAction<Response<Device>>) => {
        if (state.devices) {
          const nextDevices = ([] as Device[]).concat(state.devices);

          action?.payload?.data?.edges.forEach((item) => {
            nextDevices.push(item.node);
          });

          state.devices = nextDevices;
        } else {
          state.devices = action?.payload?.data?.edges.map((item) => item.node);
        }
        state.devicesPagination = action?.payload?.data?.pageInfo;
      },
    );
    builder.addMatcher(
      profileApi.endpoints.getProfileDeviceSessions.matchFulfilled,
      (state, action: PayloadAction<Response<DeviceSession>>) => {
        state.deviceSessions = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );

        state.deviceSessionsPagination = action.payload.data.pageInfo;
      },
    );
  },
});

export const {
  clearDevices,
  setBrowserNotificationEnabled,
  clearDeviceSession,
} = employeeProfileSlice.actions;

export default employeeProfileSlice.reducer;
